import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

export const Navbar = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "home", label: t.nav.home },
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "projects", label: t.nav.projects || "Projects" },
    { id: "clients", label: t.nav.clients },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#EAF1E8]/85 border-b border-[#183321]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo-button"
          onClick={() => handleNav("home")}
          className="flex items-baseline gap-1 group"
        >
          <span className="font-serif text-xl md:text-2xl font-semibold tracking-[0.02em] text-[#183321] uppercase">
            Josengineer
          </span>
          <span className="font-serif text-xl md:text-2xl font-medium italic text-[#2A4D35] group-hover:text-[#C26D5C] transition-colors uppercase tracking-wider">
            Corp
          </span>
          <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#C26D5C]" />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="text-[13px] tracking-wide text-[#183321] hover:text-[#C26D5C] transition-colors font-medium uppercase"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <div
            data-testid="lang-toggle"
            className="flex items-center gap-1 text-xs font-semibold tracking-widest"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${
                lang === "en"
                  ? "text-[#183321]"
                  : "text-[#183321]/40 hover:text-[#183321]/70"
              }`}
            >
              EN
            </button>
            <span className="text-[#183321]/30">/</span>
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2 py-1 transition-colors ${
                lang === "es"
                  ? "text-[#183321]"
                  : "text-[#183321]/40 hover:text-[#183321]/70"
              }`}
            >
              ES
            </button>
          </div>
          <button
            data-testid="navbar-cta"
            onClick={() => handleNav("contact")}
            className="px-5 py-2.5 bg-[#2A4D35] text-[#FDFDF9] text-xs uppercase tracking-widest font-semibold rounded-sm hover:bg-[#3C664A] hover:-translate-y-0.5 transition-all"
          >
            {t.nav.contact}
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[#183321]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#EAF1E8] border-t border-[#183321]/10 px-6 py-6"
        >
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-left font-serif text-2xl text-[#183321]"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-4 border-t border-[#183321]/10">
              <button
                data-testid="mobile-lang-en"
                onClick={() => setLang("en")}
                className={`text-xs font-semibold tracking-widest ${
                  lang === "en" ? "text-[#183321]" : "text-[#183321]/40"
                }`}
              >
                EN
              </button>
              <span className="text-[#183321]/30">/</span>
              <button
                data-testid="mobile-lang-es"
                onClick={() => setLang("es")}
                className={`text-xs font-semibold tracking-widest ${
                  lang === "es" ? "text-[#183321]" : "text-[#183321]/40"
                }`}
              >
                ES
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
